import { api, handleApiCall, ApiResult } from './api';
import { 
  StockDto, 
  PurchaseDto, 
  DividendDto,
  SaleDto,
  StockAnalysisDto,
  PortfolioSummaryDto,
  UpdateSaleCommand,
  CreateSaleCommand,
  CreateStockCommand,
  UpdateStockCommand,
  CreatePurchaseCommand
} from '../types/stocks';

// Re-export types for components that need them
export type { StockDto, PurchaseDto, DividendDto, SaleDto, StockAnalysisDto, PortfolioSummaryDto };

export async function getStocks(): Promise<StockDto[]> {
  const res = await handleApiCall(() => api.get<ApiResult<StockDto[]>>('/api/stock'));
  // Normalize potential string enum from backend to numeric InvestmentType for frontend filtering
  return (res as any[]).map((s: any) => {
    const it = s.investmentType;
    let normalized = it;
    if (typeof it === 'string') {
      const upper = it.toUpperCase();
      switch (upper) {
        case 'STOCK': normalized = 1; break;
        case 'ETF': normalized = 2; break;
        case 'BOND': normalized = 3; break;
        case 'MUTUALFUND': normalized = 4; break;
        case 'CRYPTOCURRENCY': normalized = 5; break;
        case 'REALESTATE': normalized = 6; break;
        case 'COMMODITY': normalized = 7; break;
        case 'CFD': normalized = 8; break;
        default: normalized = 1;
      }
    }
    return { ...s, investmentType: normalized } as StockDto;
  });
}

export async function getStock(publicId: string): Promise<StockDto> {
  return handleApiCall(() => api.get<ApiResult<StockDto>>(`/api/stock/by-id/${publicId}`));
}

export async function createStock(command: CreateStockCommand): Promise<StockDto> {
  return handleApiCall(() => api.post<ApiResult<StockDto>>('/api/stock', command));
}

export async function updateStock(publicId: string, command: UpdateStockCommand): Promise<StockDto> {
  return handleApiCall(() => api.put<ApiResult<StockDto>>(`/api/stock/${publicId}`, command));
}

export async function deleteStock(publicId: string): Promise<void> {
  return handleApiCall(() => api.delete<ApiResult<void>>(`/api/stock/${publicId}`));
}

// Purchase API functions
export async function createPurchase(stockPublicId: string, command: CreatePurchaseCommand): Promise<PurchaseDto> {
  return handleApiCall(() => api.post<ApiResult<PurchaseDto>>(`/api/stock/${stockPublicId}/purchase`, command));
}

export async function updatePurchase(purchasePublicId: string, command: Partial<CreatePurchaseCommand>): Promise<PurchaseDto> {
  // Backend expects EditPurchaseCommand with date, quantity, pricePerShare; include optional cambio support
  return handleApiCall(() => api.put<ApiResult<PurchaseDto>>(`/api/stock/purchase/${purchasePublicId}`, command));
}

// Dividend API functions
export async function createDividend(stockPublicId: string, dividend: Omit<DividendDto, 'publicId'>): Promise<DividendDto> {
  return handleApiCall(() => api.post<ApiResult<DividendDto>>(`/api/stock/${stockPublicId}/dividend`, dividend));
}

export async function updateDividend(dividendPublicId: string, dividend: DividendDto): Promise<DividendDto> {
  return handleApiCall(() => api.put<ApiResult<DividendDto>>(`/api/stock/dividend/${dividendPublicId}`, dividend));
}

export async function deleteDividend(dividendPublicId: string): Promise<boolean> {
  return handleApiCall(() => api.delete<ApiResult<boolean>>(`/api/stock/dividend/${dividendPublicId}`));
}

// Sale API functions
export async function createSaleBySymbol(symbol: string, sale: CreateSaleCommand): Promise<SaleDto> {
  return handleApiCall(() => api.post<ApiResult<SaleDto>>(`/api/stock/symbol/${symbol}/sale`, sale));
}

export async function updateSale(salePublicId: string, sale: UpdateSaleCommand): Promise<SaleDto> {
  return handleApiCall(() => api.put<ApiResult<SaleDto>>(`/api/stock/sale/${salePublicId}`, sale));
}

export async function deleteSale(salePublicId: string): Promise<boolean> {
  return handleApiCall(() => api.delete<ApiResult<boolean>>(`/api/stock/sale/${salePublicId}`));
}
